import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { motion } from "motion/react";
import { useAuth } from "@ncp/api-client";
import { AppVersion, StaffSignIn } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Stagger, fadeUp, getSafeRedirect } from "@ncp/ui";

/**
 * Where to land after signing in: the protected page the guard bounced from (router state), the page an expired
 * session was interrupted on (`?from=`), or the command centre. Only in-app paths are honoured.
 */
function useLoginDestination(): string {
  const location = useLocation();
  const [params] = useSearchParams();
  const from = (location.state as { from?: unknown } | null)?.from ?? params.get("from");
  return getSafeRedirect(from, "/");
}

/**
 * Case Management sign-in (security §14.1): the work e-mail or username and the password; a first sign-in replaces the temporary password.
 * No personas, demo identities or shared links; the Ministry account appears only where it is configured. One title and the form, nothing else.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { locale, toggle } = useLocale();
  const auth = useAuth();
  const navigate = useNavigate();
  const destination = useLoginDestination();

  // Only a staff session may leave the login page: a non-staff session would otherwise bounce between here and RequireStaff forever.
  const isStaff = auth.isAuthenticated && auth.user?.kind === "Staff";
  useEffect(() => {
    if (isStaff) navigate(destination, { replace: true });
  }, [isStaff, destination, navigate]);

  return (
    <div className="relative min-h-dvh overflow-hidden">
      <div className="relative mx-auto flex min-h-dvh max-w-6xl flex-col px-6 py-6 sm:px-10">
        <motion.header className="flex items-center justify-between gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
          <img src={locale === "ar" ? `${import.meta.env.BASE_URL}brand/moet-logo-ar.png` : `${import.meta.env.BASE_URL}brand/moet-logo-en.png`} alt={t("app.ministry")} className="h-10 w-auto" />
          <Button variant="ghost" size="sm" onClick={toggle}>
            <span lang={locale === "ar" ? "en" : "ar"}>{locale === "ar" ? "English" : "العربية"}</span>
          </Button>
        </motion.header>

        <main className="flex flex-1 flex-col items-center justify-center py-10">
          <Stagger className="flex w-full max-w-md flex-col items-center text-center">
            <motion.p variants={fadeUp} className="text-xs font-semibold uppercase tracking-[0.2em] text-aegold-700 rtl:text-[13px] rtl:normal-case rtl:tracking-normal">
              {t("app.backofficeTitle")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="mt-3 text-3xl font-semibold text-aeblack-950">
              {t("backoffice:login.title")}
            </motion.h1>
            <motion.div variants={fadeUp} className="mt-8 w-full">
              <StaffSignIn surface="backoffice" from={destination} onSignedIn={() => navigate(destination, { replace: true })} />
            </motion.div>
          </Stagger>
        </main>

        <motion.footer className="flex flex-col items-center gap-1 text-xs text-sand-600 sm:flex-row sm:justify-between" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.3 }}>
          <span>
            {t("app.ministry")} · {t("app.department")}
          </span>
          <AppVersion autoOpen={false} className="px-1 py-0" />
        </motion.footer>
      </div>
    </div>
  );
}
